import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { UseOutletContextPayroll } from './Payroll';
import PayrollAccessDenied from './PayrollAccessDenied';
import MonthlyDeclarationTable from './MonthlyDeclarationTable';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { PayrollRun, PayrollRunStatus, Payslip } from '../../../models/payroll-run.model';
import { PayrollRunDA } from '../../../services/admin/payroll-run.service';
import { formatMoney } from '../../../scripts/format.value.script';
import { fetchDocumentPdf } from '../../../scripts/document.pdf.script';
import { usePdfViewer } from '../../../contexts/PdfViewerContext';
import { GlobalSwal } from '../../../styles/swal.style';
import AppPreloader from '../../../components/Preloader';

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

function monthName(lang: any, month: number): string {
  return lang.labels[MONTH_KEYS[month - 1]] ?? String(month);
}

function statusBadgeClass(status: PayrollRunStatus): string {
  switch (status) {
    case 'draft': return 'badge-light-warning';
    case 'calculating': return 'badge-light-info';
    case 'calculated': return 'badge-light-primary';
    case 'validated': return 'badge-light-success';
    case 'paid': return 'badge-light-success';
    case 'cancelled': return 'badge-light-secondary';
    default: return 'badge-light-primary';
  }
}

function statusLabel(lang: any, status: PayrollRunStatus): string {
  const key = `runStatus${status.charAt(0).toUpperCase() + status.slice(1)}` as keyof typeof lang.labels;
  return lang.labels[key] ?? status;
}

function exclusionLabel(lang: any, reason: string): string {
  const map: Record<string, string> = {
    status_draft: lang.labels.exclusionStatusDraft,
    status_terminated: lang.labels.exclusionStatusTerminated,
    no_contract: lang.labels.exclusionNoContract,
    hire_after_period: lang.labels.exclusionHireAfterPeriod,
    contract_ended_before_period: lang.labels.exclusionContractEndedBeforePeriod,
  };
  return map[reason] ?? reason;
}

function warningLabel(lang: any, code: string): string {
  const map: Record<string, string> = {
    lpp_not_verifiable_hourly: lang.labels.warningLppNotVerifiableHourly,
    pdf_generation_failed: lang.labels.warningPdfGenerationFailed,
    has_absence: lang.labels.warningHasAbsence,
    hourly_absence_not_deducted: lang.labels.warningHourlyAbsenceNotDeducted,
    hourly_zero_gross: lang.labels.warningHourlyZeroGross,
  };
  return map[code] ?? code;
}

function payslipEmployeeName(payslip: Payslip): string {
  const snap = payslip.employee_snapshot;
  if (!snap) return '-';
  const cs = snap.contact_snapshot ?? snap;
  return `${cs.first_name ?? ''} ${cs.last_name ?? ''}`.trim() || snap.employee_number || '-';
}

function payslipGross(payslip: Payslip): number | null {
  return payslip.calculation_result?.gross ?? payslip.calculation_result?.totals?.gross ?? null;
}

function payslipNet(payslip: Payslip): number | null {
  return payslip.calculation_result?.net ?? payslip.calculation_result?.totals?.net ?? null;
}

// Actions table: what's available per status
interface RunActions {
  canCalculate: boolean;
  canRecalculate: boolean;
  canValidate: boolean;
  canPay: boolean;
  canCancel: boolean;
  canDownload: boolean;
  canRegenerate: boolean;
  isCalculating: boolean;
}

function getActions(run: PayrollRun): RunActions {
  const s = run.status;
  return {
    canCalculate: s === 'draft',
    canRecalculate: s === 'calculated',
    canValidate: s === 'calculated' && (run.failed_count ?? 0) === 0,
    canPay: s === 'validated',
    canCancel: s === 'draft' || s === 'calculated' || s === 'validated' || s === 'paid',
    canDownload: s === 'validated' || s === 'paid',
    canRegenerate: s === 'validated',
    isCalculating: s === 'calculating',
  };
}

const POLL_INTERVAL = 5000; // 5 seconds
const POLL_TIMEOUT = 15 * 60 * 1000; // 15 minutes

const PayrollRunViewPage: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { setRoute, companyForUser } = UseOutletContextPayroll();
  const { lang } = StateLanguage();

  const [run, setRun] = useState<PayrollRun | null>(null);
  const [payslips, setPayslips] = useState<Payslip[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const { openUrl } = usePdfViewer();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);

  // ------ Load ------

  const loadRun = useCallback(async () => {
    if (!companyForUser || !id) return;
    const response = await PayrollRunDA.getPayrollRun(companyForUser.id, id);
    if (response.status === 200) {
      setRun(response.data);
      setAccessDenied(false);
      return response.data as PayrollRun;
    } else if (response.status === 403) {
      setAccessDenied(true);
    }
    return null;
  }, [companyForUser, id]);

  const loadPayslips = useCallback(async () => {
    if (!companyForUser || !id) return;
    const response = await PayrollRunDA.getPayslips(companyForUser.id, id);
    if (response.status === 200) {
      const data = response.data;
      setPayslips(Array.isArray(data) ? data : (data?.items ?? []));
    } else {
      setPayslips([]);
    }
  }, [companyForUser, id]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadRun(), loadPayslips()]);
    setLoading(false);
  }, [loadRun, loadPayslips]);

  // ------ Polling for calculating status ------

  const startPolling = useCallback(() => {
    stopPolling();
    pollStartRef.current = Date.now();
    setPollingTimedOut(false);

    pollRef.current = setInterval(async () => {
      const elapsed = Date.now() - pollStartRef.current;
      if (elapsed > POLL_TIMEOUT) {
        stopPolling();
        setPollingTimedOut(true);
        return;
      }

      const updated = await loadRun();
      if (updated && updated.status !== 'calculating') {
        stopPolling();
        loadPayslips();
      }
    }, POLL_INTERVAL);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRun, loadPayslips]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    loadAll().then(() => {
      // If the page loads and the run is calculating, start polling
    });
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, id]);

  // Start polling when run enters calculating state
  useEffect(() => {
    if (run?.status === 'calculating' && !pollRef.current) {
      startPolling();
    }
  }, [run?.status, startPolling]);

  useEffect(() => {
    setRoute({ path: { root: lang.labels.payroll, branch: lang.labels.payrollRun }, company: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------ Actions ------

  const buildCompanySnapshot = () => {
    if (!companyForUser) return undefined;
    const addr = companyForUser.address;
    return {
      name: companyForUser.name ?? '',
      register_number: companyForUser.register_number ?? '',
      address: {
        street: addr?.street ?? '',
        number: addr?.number ?? '',
        additional: addr?.additional ?? '',
        zip_code: addr?.zip_code ?? '',
        city: addr?.city ?? '',
        country: addr?.country ?? '',
      },
    };
  };

  const isCompanyIncomplete = (): boolean => {
    if (!companyForUser) return true;
    const addr = companyForUser.address;
    return !companyForUser.name || !addr?.street || !addr?.zip_code || !addr?.city;
  };

  const handleCalculate = async () => {
    if (!companyForUser || !id) return;
    setActionLoading(true);
    const snapshot = buildCompanySnapshot();
    const response = await PayrollRunDA.calculate(companyForUser.id, id, snapshot);
    if (response.status === 200) {
      toast.success(lang.labels.actionSuccessful || 'Success');
      const updated = await loadRun();
      if (updated?.status === 'calculating') {
        startPolling();
      } else {
        loadPayslips();
      }
    } else if (response.status === 400) {
      toast.error(response.data?.message || lang.labels.companySnapshotIncomplete);
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
    setActionLoading(false);
  };

  const handleValidate = async () => {
    if (!companyForUser || !id || !run) return;

    const totals = run.totals;
    const count = run.employee_count ?? 0;

    // Build confirmation HTML
    let bodyHtml = `<p class="mb-3">${(lang.labels.confirmValidateBody || '')
      .replace('{count}', String(count))
      .replace('{gross}', formatMoney(totals?.gross))
      .replace('{net}', formatMoney(totals?.net))
      .replace('{employerCost}', formatMoney(totals?.employer_cost))
    }</p>`;

    bodyHtml += `<p class="text-danger fw-bold mb-3">${lang.labels.confirmValidateIrreversible}</p>`;

    // Final settlements check
    const finalSettlementCount = (payslips ?? []).filter(p =>
      p.contract_snapshot?.end_date &&
      p.contract_snapshot.end_date <= `${run.period.year}-${String(run.period.month).padStart(2, '0')}-31`
    ).length;
    if (finalSettlementCount > 0) {
      bodyHtml += `<p class="text-warning fw-bold">${(lang.labels.confirmValidateFinalSettlements || '').replace('{count}', String(finalSettlementCount))}</p>`;
    }

    // Negative nets check
    const negativeCount = (payslips ?? []).filter(p => {
      const net = payslipNet(p);
      return net != null && net < 0;
    }).length;
    if (negativeCount > 0) {
      bodyHtml += `<p class="text-warning fw-bold">${(lang.labels.confirmValidateNegativeNets || '').replace('{count}', String(negativeCount))}</p>`;
    }

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmValidateTitle,
      html: bodyHtml,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.validate,
      cancelButtonText: lang.labels.cancel,
    });

    if (!result.isConfirmed) return;

    setActionLoading(true);
    const response = await PayrollRunDA.validate(companyForUser.id, id);
    if (response.status === 200) {
      toast.success(lang.labels.actionSuccessful || 'Success');
      loadAll();
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
    setActionLoading(false);
  };

  const handleCancel = async () => {
    if (!companyForUser || !id) return;

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmCancelTitle,
      input: 'textarea',
      inputLabel: lang.labels.cancellationReason,
      inputPlaceholder: lang.labels.cancellationReasonRequired,
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return lang.labels.cancellationReasonRequired;
        }
        return null;
      },
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.cancelRun,
      cancelButtonText: lang.labels.cancel,
    });

    if (!result.isConfirmed || !result.value) return;

    setActionLoading(true);
    const response = await PayrollRunDA.cancel(companyForUser.id, id, { reason: result.value });
    if (response.status === 200) {
      toast.success(lang.labels.actionSuccessful || 'Success');
      loadAll();
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
    setActionLoading(false);
  };

  const handlePay = async () => {
    if (!companyForUser || !id || !run) return;

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmPayTitle,
      text: (lang.labels.confirmPayBody || '')
        .replace('{count}', String(run.employee_count ?? 0))
        .replace('{net}', formatMoney(run.totals?.net)),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.markAsPaid,
      cancelButtonText: lang.labels.cancel,
    });

    if (!result.isConfirmed) return;

    setActionLoading(true);
    const response = await PayrollRunDA.pay(companyForUser.id, id);
    if (response.status === 200) {
      toast.success(lang.labels.actionSuccessful || 'Success');
      loadRun();
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
    setActionLoading(false);
  };

  const extractPdfError = async (err: any): Promise<string> => {
    const errorData = err?.response?.data;
    if (errorData instanceof Blob) {
      try {
        const text = await errorData.text();
        const parsed = JSON.parse(text);
        return parsed?.error?.message ?? parsed?.message ?? text;
      } catch { /* not JSON */ }
    }
    return err?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain;
  };

  const handleViewPayslip = async (payslip: Payslip) => {
    if (!companyForUser || !run) return;
    setPdfLoading(payslip.id);
    try {
      const pdfUrl = await fetchDocumentPdf(companyForUser.id, 'payroll/payslips', payslip.id);
      const rawName = payslipEmployeeName(payslip).toLowerCase().replace(/\s+/g, '-').replace(/[/\\:*?"<>|]/g, '-');
      const filename = `bulletin-${run.period.year}-${String(run.period.month).padStart(2, '0')}-${rawName}.pdf`;
      openUrl(pdfUrl, filename);
    } catch (err: any) {
      toast.error(await extractPdfError(err));
    } finally {
      setPdfLoading(null);
    }
  };

  const handleViewSummary = async () => {
    if (!companyForUser || !id || !run) return;
    setPdfLoading('summary');
    try {
      const pdfUrl = await fetchDocumentPdf(companyForUser.id, 'payroll/payroll-runs', `${id}/summary`);
      const filename = `recapitulatif-${run.period.year}-${String(run.period.month).padStart(2, '0')}.pdf`;
      openUrl(pdfUrl, filename);
    } catch (err: any) {
      toast.error(await extractPdfError(err));
    } finally {
      setPdfLoading(null);
    }
  };

  const handleRegenerate = async () => {
    if (!companyForUser || !id) return;
    setActionLoading(true);
    const response = await PayrollRunDA.regeneratePdfs(companyForUser.id, id);
    if (response.status === 200) {
      toast.success(lang.labels.actionSuccessful || 'Success');
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
    setActionLoading(false);
  };

  // ------ Render ------

  if (accessDenied) return <PayrollAccessDenied />;
  if (loading || !run) return <AppPreloader />;

  const actions = getActions(run);
  const excluded = run.excluded ?? [];
  const warnings = run.warnings ?? [];
  const failedCount = run.failed_count ?? 0;
  const failedPayslips = (payslips ?? []).filter(p => p.status === 'failed');
  const hasPdfWarning = warnings.some(w => w.code === 'pdf_generation_failed');

  // Negative nets and final settlements
  const negativeNets = (payslips ?? []).filter(p => {
    const net = payslipNet(p);
    return net != null && net < 0;
  });
  const finalSettlements = (payslips ?? []).filter(p =>
    p.contract_snapshot?.end_date &&
    p.contract_snapshot.end_date <= `${run.period.year}-${String(run.period.month).padStart(2, '0')}-31`
  );

  const showControlPanel = failedCount > 0 || excluded.length > 0 || warnings.length > 0
    || negativeNets.length > 0 || finalSettlements.length > 0;

  return (
    <div className="w-100 h-100">
      {/* Back link */}
      <div className="mb-5">
        <Link to="/main/payroll/runs/list" className="btn btn-sm btn-light">
          <i className="bi bi-arrow-left me-1"></i>
          {lang.labels.backToPayrollRuns}
        </Link>
      </div>

      {/* Header card */}
      <div className="card mb-5">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h3 className="fw-bolder mb-1">
                {monthName(lang, run.period.month)} {run.period.year}
              </h3>
              <span className={`badge ${statusBadgeClass(run.status)} fs-7`}>
                {statusLabel(lang, run.status)}
              </span>
              {run.run_number && (
                <span className="text-muted ms-3 fs-7">#{run.run_number}</span>
              )}
            </div>
            <div className="d-flex gap-2 flex-wrap mt-3 mt-md-0">
              {/* Calculate / Recalculate */}
              {actions.canCalculate && (
                <button className="btn btn-primary btn-sm" onClick={handleCalculate} disabled={actionLoading}>
                  {lang.labels.calculate}
                </button>
              )}
              {actions.canRecalculate && (
                <button className="btn btn-light-primary btn-sm" onClick={handleCalculate} disabled={actionLoading}>
                  {lang.labels.recalculate}
                </button>
              )}

              {/* Validate */}
              {run.status === 'calculated' && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleValidate}
                  disabled={actionLoading || !actions.canValidate}
                  title={!actions.canValidate ? lang.labels.validateBlockedByFailures : ''}
                >
                  {lang.labels.validate}
                </button>
              )}

              {/* Pay */}
              {actions.canPay && (
                <button className="btn btn-primary btn-sm" onClick={handlePay} disabled={actionLoading}>
                  {lang.labels.markAsPaid}
                </button>
              )}

              {/* View summary */}
              {actions.canDownload && (
                <button className="btn btn-light-primary btn-sm" onClick={handleViewSummary} disabled={pdfLoading === 'summary'}>
                  {pdfLoading === 'summary'
                    ? <span className="spinner-border spinner-border-sm me-1"></span>
                    : <i className="bi bi-eye me-1"></i>
                  }
                  {lang.labels.downloadSummary}
                </button>
              )}

              {/* Regenerate PDFs */}
              {actions.canRegenerate && hasPdfWarning && (
                <button className="btn btn-light-warning btn-sm" onClick={handleRegenerate} disabled={actionLoading}>
                  {lang.labels.regeneratePdfs}
                </button>
              )}

              {/* Cancel */}
              {actions.canCancel && (
                <button className="btn btn-light-danger btn-sm" onClick={handleCancel} disabled={actionLoading}>
                  {lang.labels.cancelRun}
                </button>
              )}
            </div>
          </div>

          {/* Totals */}
          {run.totals && run.status !== 'draft' && (
            <div className="row mt-5 border-top pt-5">
              <div className="col text-center">
                <span className="text-muted fw-bold d-block fs-7">{lang.labels.grossTotal}</span>
                <span className="fw-bolder fs-5">{formatMoney(run.totals.gross)}</span>
              </div>
              <div className="col text-center">
                <span className="text-muted fw-bold d-block fs-7">{lang.labels.totalDeductions}</span>
                <span className="fw-bolder fs-5">{formatMoney(run.totals.total_deductions)}</span>
              </div>
              <div className="col text-center">
                <span className="text-muted fw-bold d-block fs-7">{lang.labels.netTotal}</span>
                <span className="fw-bolder fs-5">{formatMoney(run.totals.net)}</span>
              </div>
              <div className="col text-center">
                <span className="text-muted fw-bold d-block fs-7">{lang.labels.totalEmployerCharges}</span>
                <span className="fw-bolder fs-5">{formatMoney(run.totals.total_employer_charges)}</span>
              </div>
              <div className="col text-center">
                <span className="text-muted fw-bold d-block fs-7">{lang.labels.employerCost}</span>
                <span className="fw-bolder fs-5">{formatMoney(run.totals.employer_cost)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company data incomplete warning */}
      {(actions.canCalculate || actions.canRecalculate) && isCompanyIncomplete() && (
        <div className="alert alert-warning d-flex align-items-center mb-5">
          <i className="bi bi-exclamation-triangle-fill text-warning me-3 fs-3"></i>
          <div>
            <span className="fw-bold">{lang.labels.companySnapshotIncomplete}</span>
            <Link to="/main/company/update" className="ms-2">{lang.labels.companySettings}</Link>
          </div>
        </div>
      )}

      {/* Monthly declaration — single merged table */}
      {companyForUser && id && run.status !== 'cancelled' && (
        <MonthlyDeclarationTable
          companyId={companyForUser.id}
          runId={id}
          year={run.period.year}
          month={run.period.month}
          lang={lang}
          editable={actions.canCalculate || actions.canRecalculate}
          payslips={payslips}
          onViewPayslip={handleViewPayslip}
          pdfLoading={pdfLoading}
        />
      )}

      {/* Calculating indicator */}
      {actions.isCalculating && (
        <div className="card mb-5 border border-info">
          <div className="card-body py-4">
            <div className="d-flex align-items-center">
              <div className="spinner-border text-info me-3" role="status">
                <span className="visually-hidden">{lang.labels.calculating}</span>
              </div>
              <div>
                <h5 className="fw-bolder mb-1">{lang.labels.calculating}</h5>
                <span className="text-muted fw-bold">{lang.labels.calculatingProgress}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Polling timeout */}
      {pollingTimedOut && run.status === 'calculating' && (
        <div className="card mb-5 border border-warning">
          <div className="card-body py-4">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill text-warning fs-2 me-3"></i>
                <span className="text-muted fw-bold">{lang.labels.calculatingTimeout}</span>
              </div>
              <button className="btn btn-sm btn-warning" onClick={() => { setPollingTimedOut(false); loadRun().then(r => { if (r?.status === 'calculating') startPolling(); }); }}>
                {lang.labels.retryCalculation}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation reason */}
      {run.status === 'cancelled' && run.cancellation_reason && (
        <div className="alert alert-secondary mb-5">
          <strong>{lang.labels.cancellationReason}:</strong> {run.cancellation_reason}
        </div>
      )}

      {/* Control panel */}
      {showControlPanel && run.status !== 'draft' && run.status !== 'cancelled' && (
        <div className="card mb-5">
          <div className="card-header">
            <div className="card-title">
              <h4 className="fw-bolder">{lang.labels.controlPanel}</h4>
            </div>
          </div>
          <div className="card-body">
            {/* Failed payslips */}
            {failedCount > 0 && (
              <div className="mb-5">
                <h6 className="fw-bolder text-danger">
                  <i className="bi bi-x-circle-fill text-danger me-2"></i>
                  {lang.labels.failedPayslips}
                </h6>
                <div className="alert alert-danger py-2">
                  {(lang.labels.failedPayslipsBlocking || '').replace('{count}', String(failedCount))}
                </div>
                {failedPayslips.map(p => (
                  <div key={p.id} className="d-flex align-items-center py-2 border-bottom">
                    <span className="fw-bold me-3">{payslipEmployeeName(p)}</span>
                    <span className="text-danger">{p.error?.message ?? lang.labels.payslipFailed}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Excluded employees */}
            {excluded.length > 0 && (
              <div className="mb-5">
                <h6 className="fw-bolder text-muted">
                  <i className="bi bi-person-x-fill text-muted me-2"></i>
                  {lang.labels.excludedEmployees} ({excluded.length})
                </h6>
                {excluded.map((e, i) => (
                  <div key={i} className="d-flex align-items-center py-2 border-bottom">
                    <span className="fw-bold me-3">{e.name || e.employee_number}</span>
                    <span className="text-muted">{exclusionLabel(lang, e.reason)}</span>
                    {e.reason_detail && <span className="text-muted ms-2">({e.reason_detail})</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="mb-5">
                <h6 className="fw-bolder text-warning">
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                  {lang.labels.runWarnings} ({warnings.length})
                </h6>
                {warnings.map((w, i) => (
                  <div key={i} className="d-flex align-items-center py-2 border-bottom">
                    <span className="fw-bold me-3">{w.employee_number}</span>
                    <span className="text-muted">{warningLabel(lang, w.code)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Final settlements */}
            {finalSettlements.length > 0 && (
              <div className="mb-5">
                <h6 className="fw-bolder text-info">
                  <i className="bi bi-person-check-fill text-info me-2"></i>
                  {lang.labels.finalSettlements} ({finalSettlements.length})
                </h6>
                {finalSettlements.map(p => (
                  <div key={p.id} className="d-flex align-items-center py-2 border-bottom">
                    <span className="fw-bold">{payslipEmployeeName(p)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Negative nets */}
            {negativeNets.length > 0 && (
              <div className="mb-5">
                <h6 className="fw-bolder text-warning">
                  <i className="bi bi-dash-circle-fill text-warning me-2"></i>
                  {lang.labels.negativeNets} ({negativeNets.length})
                </h6>
                {negativeNets.map(p => (
                  <div key={p.id} className="d-flex align-items-center py-2 border-bottom">
                    <span className="fw-bold me-3">{payslipEmployeeName(p)}</span>
                    <span className="text-danger">{formatMoney(payslipNet(p))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payslips table removed — merged into MonthlyDeclarationTable above */}
    </div>
  );
};

export default PayrollRunViewPage;
